import { Alert, IconButton, Snackbar, Stack, Typography } from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";
import { MdCheck, MdContentCopy } from "react-icons/md";
import { resolveRequestErrorMessage } from "../errors/requestErrorMessages";
import { useI18n } from "../i18n/i18n";
import { registerRequestErrorNotifier } from "../requests";

type RequestErrorSnackbarProviderProps = {
  children: ReactNode;
};

export const RequestErrorSnackbarProvider = ({ children }: RequestErrorSnackbarProviderProps) => {
  const { t } = useI18n();
  const copyResetTimeoutRef = useRef<null | number>(null);
  const [copiedCorrelationId, setCopiedCorrelationId] = useState(false);
  const [correlationId, setCorrelationId] = useState<null | string>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    registerRequestErrorNotifier((notification) => {
      setCopiedCorrelationId(false);
      setCorrelationId(typeof notification === "string" ? null : notification?.correlationId?.trim() || null);
      if (typeof notification === "string") {
        setMessage(notification.trim() || t("request.error.generic"));
      } else {
        setMessage(notification?.message?.trim() || resolveRequestErrorMessage(notification?.errorCode, t));
      }
      setIsOpen(true);
    });

    return () => {
      registerRequestErrorNotifier(null);
    };
  }, [t]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current !== null) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyCorrelationId = async () => {
    if (!correlationId) {
      return;
    }
    await navigator.clipboard.writeText(correlationId);
    setCopiedCorrelationId(true);
    if (copyResetTimeoutRef.current !== null) {
      window.clearTimeout(copyResetTimeoutRef.current);
    }
    copyResetTimeoutRef.current = window.setTimeout(() => {
      setCopiedCorrelationId(false);
      copyResetTimeoutRef.current = null;
    }, 1000);
  };

  return (
    <>
      {children}
      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        autoHideDuration={5000}
        onClose={() => setIsOpen(false)}
        open={isOpen}
      >
        <Alert
          action={correlationId ? (
            <IconButton
              aria-label={copiedCorrelationId ? t("admin.copy.copied") : t("admin.copy.copy")}
              color="inherit"
              onClick={() => {
                void handleCopyCorrelationId();
              }}
              size="small"
              title={copiedCorrelationId ? t("admin.copy.copied") : t("admin.copy.copy")}
            >
              {copiedCorrelationId ? <MdCheck /> : <MdContentCopy />}
            </IconButton>
          ) : undefined}
          onClose={() => setIsOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
          variant="filled"
        >
          <Stack spacing={0.25}>
            <Typography component="div" variant="body2">
              {message || t("request.error.generic")}
            </Typography>
            {correlationId ? (
              <Typography component="div" sx={{ fontSize: "0.76rem", lineHeight: 1.35, opacity: 0.92 }}>
                {t("request.error.correlationId")}: {correlationId}
              </Typography>
            ) : null}
          </Stack>
        </Alert>
      </Snackbar>
    </>
  );
};
